import React, { PropsWithChildren } from 'react';
import { useTheme } from 'styled-components';
import * as S from './ModalDrawer.styles';
import { useResponsive } from '@/hooks/useResponsive';
import { Button } from '@/components/UI/Button/Button';
import { AngleBackIcon, CloseIcon } from '@/components/UI/Icons/Icons';
import { Drawer } from '@/components/Drawer/Drawer';

interface ModalDrawerProps {
  dataTestId?: string;
  isOpen: boolean;
  onClose: () => void;
  title: string | { iconSrc: string; text: string };
  subtitle?: string | JSX.Element;
  bottom?: React.ReactNode;
  canGoBack?: boolean;
  isBodyScrollLocked?: boolean;
}

export const ModalDrawer = ({
  dataTestId,
  isOpen,
  onClose,
  title,
  children,
  subtitle,
  bottom,
  canGoBack = false,
  isBodyScrollLocked = false,
}: PropsWithChildren<ModalDrawerProps>) => {
  const { isDesktop } = useResponsive();
  const theme = useTheme();

  if (isDesktop) {
    return (
      <S.Modal
        isScrollLocked={isBodyScrollLocked}
        dataTestId={dataTestId}
        isOpen={isOpen}
        onClose={onClose}
        maxWidth={600}
        background={theme.palette.secondary.background}
      >
        {title && (
          <S.ModalHeader>
            {canGoBack && (
              <S.GoBackButton
                appearance="transparent"
                hasPadding={false}
                onClick={onClose}
              >
                <S.GoBackIcon />
              </S.GoBackButton>
            )}
            <S.ModalTitle $isCentered={canGoBack}>
              {typeof title === 'string' ? (
                title
              ) : (
                <>
                  <img
                    src={title?.iconSrc}
                    alt=""
                    width={20}
                    height={17}
                    loading="lazy"
                  />
                  {title.text}
                </>
              )}
            </S.ModalTitle>
            {!canGoBack && (
              <Button
                appearance="transparent"
                hasPadding={false}
                onClick={onClose}
              >
                <CloseIcon
                  width={20}
                  height={20}
                  fill={theme.palette.secondary.labels}
                />
              </Button>
            )}
          </S.ModalHeader>
        )}

        <S.ModalContent>
          {subtitle}
          {children}
        </S.ModalContent>

        <S.ModalFooter>{bottom}</S.ModalFooter>
      </S.Modal>
    );
  }

  return (
    <Drawer
      isHeadFixed={false}
      dataTestId={dataTestId}
      hasBottomMargin={false}
      isOpen={isOpen}
      close={onClose}
      title={title}
      subtitle={subtitle}
      {...(canGoBack && {
        left: { icon: <AngleBackIcon />, onClick: onClose },
      })}
      bottom={bottom}
      className="action"
      hasBottomBorder={false}
      closePosition={canGoBack ? 'left' : 'right'}
    >
      {children}
    </Drawer>
  );
};
